// Journal screen — PROTOTYPE (Tier 1 only, no save).
//
// Intent: get UX feedback on which fields are in the daily quick-pass before
// wiring the save flow and the full §5 schema. Tier 1 = the highest-signal
// fields for tracking Blau-flavoured inflammation. Tier 2 / 3 are stubbed.
//
// Defaults follow the "typical no-symptom day" principle: every field opens
// pre-set to the boring/normal value so a clean day is one tap (Save) — the
// parent only has to touch a field when something is actually off. The
// downstream model: any field at its default = parent reviewed and nothing
// notable; any field changed = parent flagged something. (No ambiguous
// "unchecked" state.)
//
// When the design is approved, this file becomes the real journal form with
// saveJournal() wired up and the §5 schema fields populated correctly.

import { today } from '../storage/storage.js';
import { esc, formatDate } from './util.js';

// ---- Tier 1 field options ----

const STIFFNESS = [
  { v: 0, label: 'None' },
  { v: 1, label: 'Mild' },
  { v: 2, label: 'Moderate' },
  { v: 3, label: 'Severe' },
];

const JOINTS = [
  'L hand', 'R hand', 'L wrist', 'R wrist',
  'L ankle', 'R ankle', 'L knee', 'R knee', 'Neck',
];

const RASH_STATUS = ['None', 'Fading', 'Stable', 'Spreading'];

const EYE_SIGNS = ['None', 'Redness', 'Goopy', 'Other'];

const ADL = [
  { v: 0, label: 'Same as usual' },
  { v: 1, label: 'Some trouble' },
  { v: 2, label: 'Lots of trouble' },
];

// Default state for a normal/no-symptom day. Re-initialised on every mount
// so the prototype is always tested from the right starting point.
function initialState() {
  return {
    stiffness:  0,             // None
    joints:     new Set(),     // no joints affected (empty = none)
    rashStatus: 'None',
    eyeSigns:   new Set(['None']), // explicit "None" sentinel (exclusive with others)
    adl:        0,             // Same as usual
    note:       '',
  };
}

let state = initialState();

export function renderJournal(app) {
  // Fresh defaults each time the screen is opened (prototype, no persistence).
  state = initialState();

  app.innerHTML = `
    <header>
      <h1>Today's journal</h1>
      <p class="subtitle">${esc(formatDate(today()))} · <span class="proto-tag">prototype, no save</span></p>
      <p class="muted small">Defaults set for a normal day — only tap a field when something's off.</p>
    </header>
    <main>

      <section class="card">
        <h2>Stiffness on waking</h2>
        <div class="chip-row" id="f-stiffness">
          ${STIFFNESS.map((s) => `<button class="chip" data-v="${s.v}">${esc(s.label)}</button>`).join('')}
        </div>
      </section>

      <section class="card">
        <h2>Joints affected today</h2>
        <p class="muted small">Tap each joint that looks swollen, sore, or stiff. Leave empty if all fine.</p>
        <div class="chip-row chip-row--wrap" id="f-joints">
          ${JOINTS.map((j) => `<button class="chip" data-v="${esc(j)}">${esc(j)}</button>`).join('')}
        </div>
      </section>

      <section class="card">
        <h2>Rash today</h2>
        <div class="chip-row" id="f-rash">
          ${RASH_STATUS.map((s) => `<button class="chip" data-v="${esc(s)}">${esc(s)}</button>`).join('')}
        </div>
      </section>

      <section class="card">
        <h2>Eye signs</h2>
        <p class="muted small">Tap "None" if eyes look normal; otherwise tap whichever applies.</p>
        <div class="chip-row chip-row--wrap" id="f-eyes">
          ${EYE_SIGNS.map((s) => `<button class="chip" data-v="${esc(s)}">${esc(s)}</button>`).join('')}
        </div>
      </section>

      <section class="card">
        <h2>Functional difficulty</h2>
        <p class="muted small">Stairs, gripping, squatting, getting up from the floor.</p>
        <div class="chip-row" id="f-adl">
          ${ADL.map((a) => `<button class="chip" data-v="${a.v}">${esc(a.label)}</button>`).join('')}
        </div>
      </section>

      <section class="card">
        <h2>One-line note</h2>
        <textarea id="f-note" rows="2" maxlength="240"
                  placeholder="e.g. Stiff first 20 min, then okay. Cheeks brighter today."></textarea>
      </section>

      <section class="card">
        <h2>More detail (Tier 2)</h2>
        <p class="muted small">Sleep, mood, temperature, pain proxies, recent viral exposure, purple fingers.</p>
        <button id="f-tier2-preview" class="btn btn--secondary">Preview what would expand</button>
        <div id="tier2-preview" hidden class="muted small tier2-preview"></div>
      </section>

      <section class="card">
        <h2>Clinic prep (Tier 3)</h2>
        <p class="muted small">Detailed joint specifics (e.g. R-hand 4th MCP), rash body-map, medication timing, bowel/appetite. Surfaced only when you tap "Prepare for clinic".</p>
      </section>

      <section class="card">
        <h2>Preview saved JSON</h2>
        <p class="muted small">This is what would be written to <code>journal/${esc(today())}.json</code> when save is wired.</p>
        <pre id="preview-json" class="json-preview"></pre>
      </section>

      <div class="proto-actions">
        <button class="btn btn--primary" disabled>Save (disabled — prototype)</button>
        <a class="link-btn" href="#/">← Back to home</a>
      </div>
    </main>
    <footer>
      <span class="muted">Tell me what's missing, what to drop, what's the wrong shape.</span>
    </footer>
  `;

  wireChipGroup('f-stiffness', { mode: 'single',  stateKey: 'stiffness',  numeric: true });
  wireChipGroup('f-joints',    { mode: 'multi',   stateKey: 'joints' });
  wireChipGroup('f-rash',      { mode: 'single',  stateKey: 'rashStatus' });
  wireChipGroup('f-eyes',      { mode: 'multi',   stateKey: 'eyeSigns', exclusiveValue: 'None' });
  wireChipGroup('f-adl',       { mode: 'single',  stateKey: 'adl', numeric: true });

  const note = document.getElementById('f-note');
  note.value = state.note;
  note.addEventListener('input', () => { state.note = note.value; refreshPreview(); });

  document.getElementById('f-tier2-preview').addEventListener('click', () => {
    const el = document.getElementById('tier2-preview');
    if (el.hidden) {
      el.innerHTML = tier2PreviewHTML();
      el.hidden = false;
    } else {
      el.hidden = true;
    }
  });

  refreshPreview();
  return null;
}

// ---- Chip wiring ----

function wireChipGroup(containerId, opts) {
  const host = document.getElementById(containerId);
  if (!host) return;
  const { mode, stateKey, numeric = false, exclusiveValue = null } = opts;

  syncSelected(host, stateKey, mode);

  host.addEventListener('click', (e) => {
    const btn = e.target.closest('.chip');
    if (!btn || !host.contains(btn)) return;
    const v = btn.dataset.v;

    if (mode === 'single') {
      state[stateKey] = numeric ? Number(v) : v;
    } else {
      const set = state[stateKey];
      if (exclusiveValue && v === exclusiveValue) {
        // Tapping the sentinel: clear everything else and select it.
        set.clear();
        set.add(v);
      } else {
        // Tapping a non-sentinel: remove sentinel if present, then toggle.
        if (exclusiveValue) set.delete(exclusiveValue);
        if (set.has(v)) set.delete(v); else set.add(v);
        // If they tapped the only option off and a sentinel exists, snap back.
        if (exclusiveValue && set.size === 0) set.add(exclusiveValue);
      }
    }
    syncSelected(host, stateKey, mode);
    refreshPreview();
  });
}

function syncSelected(host, stateKey, mode) {
  const current = state[stateKey];
  host.querySelectorAll('.chip').forEach((btn) => {
    const v = btn.dataset.v;
    let selected = false;
    if (mode === 'single') {
      selected = current != null && String(current) === String(v);
    } else {
      selected = current instanceof Set && current.has(v);
    }
    btn.classList.toggle('chip--selected', selected);
  });
}

function refreshPreview() {
  const el = document.getElementById('preview-json');
  if (!el) return;
  const json = {
    schemaVersion: '2026.05.1',
    date: today(),
    tier1: {
      stiffness: state.stiffness,
      joints: Array.from(state.joints),
      rashStatus: state.rashStatus,
      eyeSigns: Array.from(state.eyeSigns),
      adl: state.adl,
      note: state.note,
    },
  };
  el.textContent = JSON.stringify(json, null, 2);
}

function tier2PreviewHTML() {
  return `
    <div class="tier2-grid">
      <div>Sleep last night<br><span class="chip-row--inline">[Good] [Disturbed] [Bad]</span></div>
      <div>Mood / energy<br><span class="chip-row--inline">[Chipper] [Flat] [Withdrawn]</span></div>
      <div>Pain proxies<br><span class="chip-row--inline">[Limping] [Won't grip] [Won't pull to stand] [Won't stair]</span></div>
      <div>Temperature<br><span class="chip-row--inline">[Normal] · or enter °C</span></div>
      <div>Purple / cold fingers<br><span class="chip-row--inline">[No] [Yes]</span></div>
      <div>Recent viral exposure<br><span class="chip-row--inline">[No] [Yes — sibling cold] [Yes — daycare bug]</span></div>
      <div>Care setting today<br><span class="chip-row--inline">[Home] [Daycare] [Other]</span></div>
    </div>
  `;
}
